import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Alert } from '../../components/ui/Alert'
import { Toast } from '../../components/ui/Toast'
import { SkeletonCard } from '../../components/ui/Skeleton'
import * as productService from '../../services/productService'
import * as specialOffersService from '../../services/specialOffersService'
import { formatMoney } from '@/lib/money'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'

const DEFAULT_LIMIT = 20

type CatalogRow = {
  id: number
  name: string
  slug: string
  sku?: string
  price?: number
  isActive?: boolean
}

function rowFromItem(item: unknown): CatalogRow | null {
  if (!item || typeof item !== 'object') return null
  const o = item as Record<string, unknown>
  const id = typeof o.id === 'number' ? o.id : Number(o.id)
  if (!Number.isFinite(id)) return null
  return {
    id,
    name: String(o.name ?? ''),
    slug: String(o.slug ?? ''),
    sku: o.sku != null ? String(o.sku) : undefined,
    price: typeof o.price === 'number' ? o.price : Number(o.price),
    isActive: typeof o.isActive === 'boolean' ? o.isActive : undefined,
  }
}

export default function SpecialOfferProductsPage() {
  const { offerId: offerIdParam } = useParams()
  const [searchParams] = useSearchParams()
  const storeIdRaw = searchParams.get('storeId')

  const offerId = offerIdParam != null ? Number(offerIdParam) : NaN
  const storeId = storeIdRaw != null ? Number(storeIdRaw) : NaN
  const idsValid = Number.isFinite(offerId) && offerId >= 1 && Number.isFinite(storeId) && storeId >= 1

  const [detail, setDetail] = useState<specialOffersService.SpecialOfferDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const [rows, setRows] = useState<CatalogRow[]>([])
  const [total, setTotal] = useState(0)
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [catalogError, setCatalogError] = useState<string | null>(null)

  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null)
  const [pendingId, setPendingId] = useState<number | null>(null)

  const memberIds = useMemo(() => {
    if (!detail?.productIds?.length) return new Set<number>()
    return new Set(detail.productIds)
  }, [detail?.productIds])

  const explicitMembers = useMemo(() => {
    if (!detail) return []
    if (detail.explicitProducts?.length) return detail.explicitProducts
    return (detail.productIds ?? []).map((id) => ({
      id,
      name: `Product #${id}`,
      slug: '',
    }))
  }, [detail])

  const skipQResetRef = useRef(true)
  useEffect(() => {
    const t = window.setTimeout(() => setQ(searchInput.trim()), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    if (skipQResetRef.current) {
      skipQResetRef.current = false
      return
    }
    setPage(1)
  }, [q])

  const refreshDetail = useCallback(async () => {
    if (!idsValid) return
    setDetailError(null)
    setDetailLoading(true)
    try {
      const d = await specialOffersService.getSpecialOffer(storeId, offerId)
      setDetail(d)
    } catch (e) {
      setDetail(null)
      setDetailError(getUserFacingErrorMessage(e, 'Could not load offer'))
    } finally {
      setDetailLoading(false)
    }
  }, [idsValid, storeId, offerId])

  useEffect(() => {
    if (!idsValid) return
    void refreshDetail()
  }, [idsValid, refreshDetail])

  useEffect(() => {
    if (!idsValid) return
    let cancelled = false
    setCatalogLoading(true)
    setCatalogError(null)
    productService
      .fetchProducts({
        page,
        limit,
        q: q || undefined,
        isActive: true,
      })
      .then((res) => {
        if (cancelled) return
        const parsed = (res.items ?? []).map(rowFromItem).filter((r): r is CatalogRow => r != null)
        setRows(parsed)
        setTotal(res.total ?? 0)
      })
      .catch((e) => {
        if (!cancelled) {
          setRows([])
          setTotal(0)
          setCatalogError(getUserFacingErrorMessage(e, 'Could not load products'))
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [idsValid, page, limit, q])

  const addOne = async (productId: number) => {
    if (!idsValid) return
    setPendingId(productId)
    try {
      await specialOffersService.addOfferProducts(storeId, offerId, [productId])
      setToast({ message: 'Product added to offer', variant: 'success' })
      await refreshDetail()
    } catch (e) {
      setToast({ message: getUserFacingErrorMessage(e, 'Could not add product'), variant: 'error' })
    } finally {
      setPendingId(null)
    }
  }

  const removeOne = async (productId: number) => {
    if (!idsValid) return
    setPendingId(productId)
    try {
      await specialOffersService.removeOfferProduct(storeId, offerId, productId)
      setToast({ message: 'Product removed from offer', variant: 'success' })
      await refreshDetail()
    } catch (e) {
      setToast({ message: getUserFacingErrorMessage(e, 'Could not remove product'), variant: 'error' })
    } finally {
      setPendingId(null)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit) || 1)
  const showingFrom = total === 0 ? 0 : (page - 1) * limit + (rows.length === 0 ? 0 : 1)
  const showingTo = total === 0 ? 0 : (page - 1) * limit + rows.length

  if (!idsValid) {
    return (
      <div className="space-y-4">
        <Alert variant="warning">
          Missing or invalid store or offer. Open this page from an offer using &quot;Manage products&quot; (requires
          storeId and offer in the URL).
        </Alert>
        <Link to="/special-offers">
          <Button type="button" variant="secondary">
            Back to special offers
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {toast ? (
        <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-xs text-muted-foreground">
            <Link to="/special-offers" className="text-primary hover:underline">
              Special offers
            </Link>
            <span className="mx-1.5">/</span>
            <span>Products</span>
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
            {detailLoading && !detail ? 'Loading…' : detail?.title ?? 'Offer products'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Search your catalog and add or remove products for this deal. Only published (active) products are listed
            here.
          </p>
        </div>
        <Link to="/special-offers">
          <Button type="button" variant="secondary">
            Back to offers
          </Button>
        </Link>
      </div>

      {detailError ? <Alert variant="destructive">{detailError}</Alert> : null}

      <Card>
        <CardHeader
          title="Products in this offer"
          description={`${explicitMembers.length} product${explicitMembers.length === 1 ? '' : 's'} explicitly included. Category rules still apply from the offer editor.`}
        />
        <div className="px-6 pb-6">
          {detailLoading && !detail ? <SkeletonCard className="h-24" /> : null}
          {detail && explicitMembers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No products yet. Add some from the catalog below.</p>
          ) : null}
          {detail && explicitMembers.length > 0 ? (
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[480px] text-left text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="px-4 py-3 font-semibold">S/N</th>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Slug</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {explicitMembers.map((p, rowIndex) => (
                    <tr key={p.id} className="border-b border-border/80 last:border-0">
                      <td className="px-4 py-3 tabular-nums">{rowIndex + 1}</td>
                      <td className="px-4 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.slug || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          type="button"
                          variant="danger"
                          className="text-xs"
                          disabled={pendingId === p.id}
                          onClick={() => void removeOne(p.id)}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </Card>

      <Card>
        <CardHeader title="Browse catalog" description="Search by product name, then add to the offer." />
        <div className="px-6 pb-6 space-y-4">
          <div className="max-w-md">
            <Input
              id="offer-product-search"
              label="Search"
              placeholder="Product name…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
            />
          </div>
          {catalogError ? <Alert variant="destructive">{catalogError}</Alert> : null}
          {catalogLoading ? <SkeletonCard className="h-40" /> : null}
          {!catalogLoading ? (
            <>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                    <th className="px-4 py-3 font-semibold">S/N</th>
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">Slug</th>
                      <th className="px-4 py-3 font-semibold">Price</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold text-right">Offer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item, rowIndex) => {
                      const inOffer = memberIds.has(item.id)
                      return (
                        <tr key={item.id} className="border-b border-border/80 last:border-0">
                          <td className="px-4 py-3 tabular-nums">{(page - 1) * limit + rowIndex + 1}</td>
                          <td className="px-4 py-3 font-medium">{item.name}</td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{item.slug}</td>
                          <td className="px-4 py-3 tabular-nums">
                            {item.price != null && Number.isFinite(item.price) ? formatMoney(item.price) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {item.isActive ? (
                              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                                Active
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {inOffer ? (
                              <Button
                                type="button"
                                variant="secondary"
                                className="text-xs"
                                disabled={pendingId === item.id}
                                onClick={() => void removeOne(item.id)}
                              >
                                Remove
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                className="text-xs"
                                disabled={pendingId === item.id}
                                onClick={() => void addOne(item.id)}
                              >
                                Add
                              </Button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No products match this search.</p>
              ) : null}
              {total > 0 ? (
                <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-muted-foreground">
                    Showing{' '}
                    <span className="font-medium text-foreground">{showingFrom}</span>–
                    <span className="font-medium text-foreground">{showingTo}</span> of{' '}
                    <span className="font-medium text-foreground">{total}</span>
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      Per page
                      <select
                        className="rounded-lg border border-border bg-card px-2 py-1.5 text-sm text-foreground"
                        value={limit}
                        disabled={catalogLoading}
                        onChange={(e) => {
                          setLimit(Number(e.target.value))
                          setPage(1)
                        }}
                      >
                        {[10, 20, 50, 100].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                    <Button
                      type="button"
                      variant="secondary"
                      className="!px-3 !py-1.5 text-xs"
                      disabled={page <= 1 || catalogLoading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span className="text-xs font-medium text-foreground">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      className="!px-3 !py-1.5 text-xs"
                      disabled={page >= totalPages || catalogLoading}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </Card>
    </div>
  )
}
